import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';

import AccordionWrapper from 'components/ChecklistReport/AccordionWrapper';
import { scoreSummaryStyles, ScoreHeaderCnt } from './scoreSummary/styles';
import {
  getMatchingScoreConfig,
  COLORING_SCHEME,
} from 'utils/templates/scoringConfig';
import { checkVisibilityWithText } from 'components/TaskChecklist/utils';
import { getOverallPercentage } from 'utils/templates';

interface ScoreData {
  percentage: number;
  earned: number;
  total: number;
}

interface ScoreItem {
  id: string;
  description?: string;
  title?: string;
  score?: ScoreData;
  subSections?: ScoreItem[];
}

interface ScoreSummaryProps {
  items: ScoreItem[];
  reference: React.RefObject<HTMLDivElement>;
  log: {
    score?: ScoreData;
  };
  template?: any;
  conditions: any;
  checkListLogHashes: any;
  locationGroupHash: any;
}

const DEFAULT_COLOR = '#1f1e1ee0';

// Helper function to check if an item has a valid score
const hasValidScore = (item: ScoreItem): boolean => {
  return Boolean(item.score?.total && item.score.total > 0);
};

// Helper function to get display text for an item
const getItemDisplayText = (item: ScoreItem): string => {
  return item.description || item.title || 'Untitled';
};

// Optimized recursive function to get items with scores
const getItemsWithScores = (items: ScoreItem[]): ScoreItem[] => {
  const result: ScoreItem[] = [];

  for (const item of items) {
    if (hasValidScore(item)) {
      result.push(item);
    } else if (item.subSections?.length) {
      const scoredSubItems = getItemsWithScores(item.subSections);
      if (scoredSubItems.length > 0) {
        result.push({
          ...item,
          subSections: scoredSubItems,
        });
      }
    }
  }

  return result;
};

// Optimized recursive function to calculate total scores
const calculateTotalScore = (items: ScoreItem[]): ScoreData => {
  let totalEarned = 0;
  let totalPoints = 0;

  const processItems = (itemList: ScoreItem[]) => {
    for (const item of itemList) {
      if (hasValidScore(item)) {
        totalEarned += item?.score?.earned || 0;
        totalPoints += item?.score?.total || 0;
      }
    }
  };

  processItems(items);

  return {
    earned: totalEarned,
    total: totalPoints,
    percentage:
      totalPoints > 0 ? Math.round((totalEarned / totalPoints) * 100) : 0,
  };
};

// Component to render table rows recursively
const TableRowRenderer: React.FC<{
  item: ScoreItem;
  level: number;
  template?: any;
}> = ({ item, level, template }) => {
  const hasScore = hasValidScore(item);
  const scoredSubSections = useMemo(() => {
    return item.subSections?.filter(hasValidScore) || [];
  }, [item.subSections]);
  const percentage = getOverallPercentage(item?.score);

  const matchedConfig = getMatchingScoreConfig(
    percentage,
    template?.HotelChecklist?.scoreConfig,
  );

  return (
    <>
      {hasScore && (
        <tr style={scoreSummaryStyles.tableRow(level)}>
          <td style={scoreSummaryStyles.tableCell(level)}>
            {getItemDisplayText(item)}
          </td>
          <td
            style={{
              ...scoreSummaryStyles.tableCellNoIndent(level),
              ...scoreSummaryStyles.tableCellCenter,
            }}
          >
            {item?.score?.earned || 0}
          </td>
          <td
            style={{
              ...scoreSummaryStyles.tableCellNoIndent(level),
              ...scoreSummaryStyles.tableCellCenter,
            }}
          >
            {item?.score?.total || 0}
          </td>
          <td
            style={{
              ...scoreSummaryStyles.tableCellNoIndent(level),
              ...scoreSummaryStyles.tableCellCenter,
            }}
          >
            {percentage}%
          </td>
          <td
            style={{
              ...scoreSummaryStyles.tableCellNoIndent(level),
              ...scoreSummaryStyles.tableCellCenter,
            }}
          >
            {matchedConfig?.rating ? (
              <Box
                component="span"
                sx={{
                  ...scoreSummaryStyles.ratingChip(percentage),
                  backgroundColor: `${
                    matchedConfig?.color ?? DEFAULT_COLOR
                  } !important`,
                  color: 'white !important',
                }}
              >
                {matchedConfig?.rating}
              </Box>
            ) : (
              // Empty cell for sub-sections and individual items
              <span>-</span>
            )}
          </td>
        </tr>
      )}
      {scoredSubSections.map((subItem) => (
        <TableRowRenderer
          key={subItem.id}
          item={subItem}
          level={level + 1}
          template={template}
        />
      ))}
    </>
  );
};

const ScoreSummary: React.FC<ScoreSummaryProps> = ({
  items,
  reference,
  log,
  template,
  conditions,
  checkListLogHashes,
  locationGroupHash,
}) => {
  const scoredItems = useMemo(() => getItemsWithScores(items || []), [items]);

  const remainingScoreItem = useMemo((): ScoreItem | null => {
    if (!log?.score?.total) return null;

    const totalScoredItems = calculateTotalScore(scoredItems);
    const remainingEarned = log.score.earned - totalScoredItems.earned;
    const remainingTotal = log.score.total - totalScoredItems.total;

    if (remainingTotal <= 0) return null;

    const remainingPercentage = Math.round(
      (remainingEarned / remainingTotal) * 100,
    );

    return {
      id: 'untitled-sections',
      title: 'Untitled Sections',
      score: {
        percentage: remainingPercentage,
        earned: remainingEarned,
        total: remainingTotal,
      },
    };
  }, [log?.score, scoredItems]);

  const itemsToDisplay = useMemo(() => {
    return remainingScoreItem
      ? [remainingScoreItem, ...scoredItems]
      : scoredItems;
  }, [remainingScoreItem, scoredItems]);

  const hasOverallScore = Boolean(log?.score?.total && log.score.total >= 0);
  const overallPercentage = getOverallPercentage(log?.score);

  const matchedConfig = getMatchingScoreConfig(
    overallPercentage,
    template?.HotelChecklist?.scoreConfig,
  );

  if (!hasOverallScore) {
    return (
      <Box className="mb-3" id="scoreSummary" ref={reference}>
        <AccordionWrapper
          headerName={
            <Typography variant="h6" fontWeight={600} color="#1A1A1A">
              Score Summary
            </Typography>
          }
        >
          <Box sx={scoreSummaryStyles.emptyState}>
            <Typography variant="body2">No scored items available</Typography>
          </Box>
        </AccordionWrapper>
      </Box>
    );
  }

  return (
    <ScoreHeaderCnt className="mb-3" id="scoreSummary" ref={reference}>
      <Box className="scoreContainer">
        <Box className="summaryBar">
          <Box
            className="summaryBarLeft"
            sx={{
              backgroundColor: `${
                matchedConfig?.color ?? DEFAULT_COLOR
              } !important`,
            }}
          >
            <Typography className="summaryBarLeftTitle">
              {matchedConfig?.rating ?? 'Score'}
            </Typography>
            <Typography className="summaryBarLeftSubtitle">
              {overallPercentage}%
            </Typography>
          </Box>
          <Box
            className="summaryBarRight"
            sx={{
              backgroundColor: `${
                COLORING_SCHEME[matchedConfig?.color ?? DEFAULT_COLOR]
                  ?.secondaryColor
              } !important`,
              borderColor: `${
                COLORING_SCHEME[matchedConfig?.color ?? DEFAULT_COLOR]
                  ?.borderColor
              } !important`,
            }}
          >
            <Box className="summaryBarRightContent">
              <Typography
                className="overallScoreText"
                sx={{
                  color: `${
                    COLORING_SCHEME[matchedConfig?.color ?? DEFAULT_COLOR]
                      ?.labelColor
                  } !important`,
                }}
              >
                Overall Score
              </Typography>
              <Typography className="overallScoreValue">
                <strong
                  className="value"
                  style={{
                    color: `${
                      COLORING_SCHEME[matchedConfig?.color ?? DEFAULT_COLOR]
                        ?.labelColor
                    } !important`,
                  }}
                >
                  {log?.score?.earned}
                </strong>{' '}
                Points Earned &nbsp;&nbsp;&nbsp;
                <strong className="value">{log?.score?.total}</strong> Total
                Points
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Score Summary with Accordion */}
        <AccordionWrapper
          headerName={
            <Typography variant="h6" fontWeight={600} color="#1A1A1A">
              Score Summary
            </Typography>
          }
          headerEndRenderer={
            <Box
              component="span"
              sx={{
                ...scoreSummaryStyles.overallScoreChip(overallPercentage),
                backgroundColor: `${
                  matchedConfig?.color ?? DEFAULT_COLOR
                } !important`,
                color: 'white !important',
              }}
            >
              {matchedConfig?.rating ?? 'Score'} • {overallPercentage}% (
              {log?.score?.earned} of {log?.score?.total} pts)
            </Box>
          }
        >
          <Box className="tableContainer">
            {itemsToDisplay.length > 0 ? (
              <table className="table">
                <thead className="tableHeader">
                  <tr>
                    <th
                      className="tableHeaderCell"
                      style={{ textAlign: 'left' }}
                    >
                      Section/Sub Section
                    </th>
                    <th className="tableHeaderCell">Points Earned</th>
                    <th className="tableHeaderCell">Total Points</th>
                    <th className="tableHeaderCell">Score</th>
                    <th className="tableHeaderCell">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsToDisplay.map((item) => {
                    const isHidden = checkVisibilityWithText(
                      conditions,
                      item,
                      checkListLogHashes,
                      true,
                      locationGroupHash,
                    );

                    if (isHidden) return null;

                    return (
                      <TableRowRenderer
                        key={item.id}
                        item={item}
                        level={0}
                        template={template}
                      />
                    );
                  })}
                  {/* Total Row */}
                  <tr
                    style={{
                      ...scoreSummaryStyles.tableRow(0),
                      backgroundColor: '#F8F9FA',
                      fontWeight: 800,
                    }}
                  >
                    <td
                      style={{
                        ...scoreSummaryStyles.tableCell(0),
                        fontWeight: 800,
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        ...scoreSummaryStyles.tableCellNoIndent(0),
                        ...scoreSummaryStyles.tableCellCenter,
                        fontWeight: 800,
                      }}
                    >
                      {log?.score?.earned}
                    </td>
                    <td
                      style={{
                        ...scoreSummaryStyles.tableCellNoIndent(0),
                        ...scoreSummaryStyles.tableCellCenter,
                        fontWeight: 800,
                      }}
                    >
                      {log?.score?.total}
                    </td>
                    <td
                      style={{
                        ...scoreSummaryStyles.tableCellNoIndent(0),
                        ...scoreSummaryStyles.tableCellCenter,
                        fontWeight: 800,
                      }}
                    >
                      {overallPercentage}%
                    </td>
                    <td
                      style={{
                        ...scoreSummaryStyles.tableCellNoIndent(0),
                        ...scoreSummaryStyles.tableCellCenter,
                      }}
                    >
                      {matchedConfig?.rating ? (
                        <Box
                          component="span"
                          sx={{
                            ...scoreSummaryStyles.ratingChip(overallPercentage),
                            backgroundColor: `${
                              matchedConfig?.color ?? DEFAULT_COLOR
                            } !important`,
                            color: 'white !important',
                          }}
                        >
                          {matchedConfig?.rating}
                        </Box>
                      ) : (
                        <>-</>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <Box sx={scoreSummaryStyles.emptyState}>
                <Typography variant="body2">
                  No scored items available
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionWrapper>
      </Box>
    </ScoreHeaderCnt>
  );
};

export default ScoreSummary;
